import { useState, useEffect } from "react";
import { Menu, X, ChevronDown } from "lucide-react";
import Logo from "../Logo";
import { MobileMenu } from "./MobileMenu";
import { ContactManager } from "../ContactManager";

const LangSwitcher = ({ lang, navigateLang, onDark = false }) => {
  const [open, setOpen] = useState(false);
  const items = [
    { code: "en", label: "EN" },
    { code: "uk", label: "UA" },
  ];
  return (
    <div
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      data-testid="lang-switcher"
    >
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        data-testid="lang-current"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5"
        style={{
          fontFamily: "var(--font-body)",
          fontSize: 13,
          fontWeight: 700,
          letterSpacing: "0.18em",
          textTransform: "uppercase",
          color: onDark ? "var(--white)" : "var(--ink)",
          padding: "10px 12px",
          background: "transparent",
          cursor: "pointer",
        }}
      >
        {lang === "uk" ? "UA" : "EN"}
        <ChevronDown
          size={13}
          style={{
            color: "var(--gold-500)",
            transform: open ? "rotate(180deg)" : "rotate(0)",
            transition: "transform var(--dur) var(--ease)",
          }}
        />
      </button>
      <div
        role="listbox"
        data-testid="lang-options"
        className={`absolute right-0 top-full pt-3 ${
          open ? "opacity-100 visible" : "opacity-0 invisible"
        }`}
        style={{
          transition: "opacity var(--dur) var(--ease), visibility var(--dur)",
          minWidth: 120,
        }}
      >
        <ul
          className="py-2"
          style={{
            background: "var(--white)",
            borderRadius: "var(--radius-md)",
            boxShadow: "var(--shadow-md)",
            border: "1px solid var(--border)",
            listStyle: "none",
            margin: 0,
            padding: "8px 0",
          }}
        >
          {items.map((it) => {
            const active = it.code === lang;
            return (
              <li key={it.code}>
                <button
                  type="button"
                  data-testid={`lang-${it.code}`}
                  onClick={() => {
                    setOpen(false);
                    if (!active) navigateLang(it.code);
                  }}
                  aria-selected={active}
                  className="under-link"
                  style={{
                    display: "block",
                    width: "100%",
                    textAlign: "left",
                    padding: "8px 18px",
                    fontFamily: "var(--font-body)",
                    fontSize: 13,
                    fontWeight: 700,
                    letterSpacing: "0.18em",
                    textTransform: "uppercase",
                    color: active ? "var(--gold-500)" : "var(--ink)",
                    background: "transparent",
                    border: 0,
                    cursor: "pointer",
                    transition: "color var(--dur) var(--ease)",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = "var(--gold-500)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = active
                      ? "var(--gold-500)"
                      : "var(--ink)";
                  }}
                >
                  {it.label}
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

export const Nav = ({ t, lang, navigateLang, onServiceSelect }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { href: "#about", label: t.nav.about, key: "about" },
    { href: "#services", label: t.nav.services, key: "services" },
    { href: "#cases", label: t.nav.cases, key: "cases" },
    { href: "#contact", label: t.nav.contact, key: "contact" },
  ];

  const serviceItems = t.services.items.map((it) => ({
    key: it.key,
    label: it.title,
  }));

  const handleServiceClick = (key, e) => {
    if (e) e.preventDefault();
    setServicesOpen(false);
    setOpen(false);
    onServiceSelect(key);
  };

  return (
    <header
      data-testid="site-header"
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
      style={{
        background: scrolled ? "var(--white)" : "rgba(255,255,255,0.85)",
        backdropFilter: "blur(14px)",
        WebkitBackdropFilter: "blur(14px)",
        boxShadow: scrolled ? "var(--shadow-sm)" : "none",
        borderBottom: scrolled ? "1px solid var(--border)" : "1px solid transparent",
      }}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 h-[82px] flex items-center justify-between">
        <div className="flex items-center gap-10 md:gap-14">
          <a
            href="#top"
            data-testid="logo-link"
            className="flex items-center"
            aria-label="Kadra Talent"
          >
            <Logo variant="dark" height={72} />
          </a>

          <nav className="hidden md:flex items-center gap-9">
            {links.map((l) =>
              l.key === "services" ? (
                <div
                  key={l.href}
                  className="relative"
                  onMouseEnter={() => setServicesOpen(true)}
                  onMouseLeave={() => setServicesOpen(false)}
                  onFocus={() => setServicesOpen(true)}
                  onBlur={() => setServicesOpen(false)}
                  data-testid="nav-services-wrap"
                >
                  <a
                    href={l.href}
                    data-testid="nav-services"
                    className="under-link text-[14px] font-medium"
                    style={{ color: "var(--ink)" }}
                    aria-haspopup="true"
                    aria-expanded={servicesOpen}
                  >
                    {l.label}
                    <ChevronDown
                      size={12}
                      style={{
                        display: "inline-block",
                        marginLeft: 4,
                        verticalAlign: "middle",
                        position: "relative",
                        top: "-1px",
                        transform: servicesOpen
                          ? "rotate(180deg)"
                          : "rotate(0)",
                        transition: "transform var(--dur) var(--ease)",
                        color: "var(--gold-500)",
                      }}
                    />
                  </a>
                  <div
                    data-testid="services-submenu"
                    className={`absolute left-0 top-full pt-3 ${
                      servicesOpen
                        ? "opacity-100 visible"
                        : "opacity-0 invisible"
                    }`}
                    style={{
                      transition:
                        "opacity var(--dur) var(--ease), visibility var(--dur)",
                      minWidth: 240,
                    }}
                  >
                    <ul
                      className="py-3"
                      style={{
                        background: "var(--white)",
                        borderRadius: "var(--radius-md)",
                        boxShadow: "var(--shadow-md)",
                        border: "1px solid var(--border)",
                      }}
                    >
                      {serviceItems.map((s) => (
                        <li key={s.key}>
                          <a
                            href={`#services-${s.key}`}
                            data-testid={`submenu-${s.key}`}
                            onClick={(e) => handleServiceClick(s.key, e)}
                            className="under-link block px-5 py-2.5 text-[14px] font-medium"
                            style={{ color: "var(--ink)" }}
                          >
                            {s.label}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ) : (
                <a
                  key={l.href}
                  data-testid={`nav-${l.href.slice(1)}`}
                  href={l.href}
                  className="under-link text-[14px] font-medium"
                  style={{ color: "var(--ink)" }}
                >
                  {l.label}
                </a>
              )
            )}
          </nav>
        </div>

        <div className="flex items-center gap-3 md:gap-5">
          <ContactManager
            size="nav"
            theme="light"
            labels={t.contactManager}
          />
          <a
            href="#contact"
            data-testid="nav-cta"
            className="hidden md:inline-flex btn btn-gold"
            style={{ padding: "12px 22px", fontSize: "13px", minHeight: 44 }}
          >
            {t.nav.cta}
          </a>
          <div className="hidden md:block">
            <LangSwitcher lang={lang} navigateLang={navigateLang} />
          </div>
          <button
            data-testid="mobile-menu-toggle"
            type="button"
            className="md:hidden"
            style={{ color: "var(--green-800)" }}
            onClick={() => setOpen(!open)}
            aria-label="menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <MobileMenu
          t={t}
          lang={lang}
          links={links}
          serviceItems={serviceItems}
          navigateLang={navigateLang}
          onServiceClick={handleServiceClick}
          onClose={() => setOpen(false)}
        />
      )}
    </header>
  );
};
